import { useCallback, useEffect, useRef, useState } from "react";
import type { AgentAnswer, ChartKind } from "@brasilnovo/shared";

// Persistência LOCAL dos "estudos" de exploração (pedido: armazenar conversas no
// local). Sem ida ao servidor — histórico vive no navegador, por isso não há PII
// de operação retida no back. Uma sessão = uma conversa com o agente + gráficos.

const KEY = "bn_explorar_sessions";
// Quantas respostas recentes guardam as LINHAS completas (para re-render dos
// gráficos). Mensagens mais antigas mantêm resumo/SQL/specs, mas sem `rows` —
// evita estourar a cota do localStorage com históricos grandes (achado #6).
const ROWS_KEEP_RECENT = 12;

// Contrato único do agente (shared). `AgentPayload` é só um alias histórico.
export type AgentPayload = AgentAnswer;

export interface ExplorerMessage {
  id: string;
  role: "user" | "assistant";
  text?: string; // pergunta (user) ou resumo (assistant)
  answer?: AgentPayload; // payload do agente (assistant)
  selectedKind?: ChartKind; // gráfico ativo escolhido pelo usuário
  pending?: boolean;
  error?: string;
  ts: number;
}

export interface ExplorerSession {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  messages: ExplorerMessage[];
}

export const uid = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.floor(Math.random() * 1e9).toString(36)}`;

function load(): ExplorerSession[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ExplorerSession[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return []; // storage corrompido → começa limpo (não derruba a página)
  }
}

// Mantém as LINHAS só das N respostas mais recentes de cada estudo; as antigas
// preservam resumo/SQL/specs mas zeram `rows` (degradam para "sem gráfico" no
// reload). Limita o tamanho serializado sem perder o histórico textual.
function trimForStorage(sessions: ExplorerSession[]): ExplorerSession[] {
  return sessions.map((s) => {
    let kept = 0;
    const messages = [...s.messages]
      .reverse()
      .map((m) => {
        if (m.role === "assistant" && m.answer && m.answer.rows.length) {
          kept += 1;
          if (kept > ROWS_KEEP_RECENT) return { ...m, answer: { ...m.answer, rows: [] } };
        }
        return m;
      })
      .reverse();
    return { ...s, messages };
  });
}

// Retorna true se persistiu; false se a cota estourou (UI segue em memória).
function persist(sessions: ExplorerSession[]): boolean {
  try {
    localStorage.setItem(KEY, JSON.stringify(trimForStorage(sessions)));
    return true;
  } catch {
    return false; // cota cheia / modo privado: não derruba a UI, mas sinaliza
  }
}

export function newSession(title = "Novo estudo"): ExplorerSession {
  const now = Date.now();
  return { id: uid(), title, createdAt: now, updatedAt: now, messages: [] };
}

export interface ExplorerStore {
  sessions: ExplorerSession[];
  activeId: string | null;
  active: ExplorerSession | null;
  storageFull: boolean;
  setActiveId: (id: string) => void;
  create: () => string;
  rename: (id: string, title: string) => void;
  remove: (id: string) => void;
  addMessage: (sessionId: string, msg: ExplorerMessage) => void;
  patchMessage: (sessionId: string, msgId: string, patch: Partial<ExplorerMessage>) => void;
}

/** Hook que expõe os estudos e o CRUD, persistindo em localStorage a cada mudança. */
export function useExplorerSessions(): ExplorerStore {
  const [sessions, setSessions] = useState<ExplorerSession[]>(() => load());
  const [activeId, setActiveId] = useState<string | null>(() => load()[0]?.id ?? null);
  const [storageFull, setStorageFull] = useState(false);
  const first = useRef(true);

  // Persiste sempre que a lista muda (pula o 1º render, que só carregou).
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    setStorageFull(!persist(sessions));
  }, [sessions]);

  const touch = (s: ExplorerSession): ExplorerSession => ({ ...s, updatedAt: Date.now() });

  const create = useCallback(() => {
    const s = newSession();
    setSessions((prev) => [s, ...prev]);
    setActiveId(s.id);
    return s.id;
  }, []);

  const rename = useCallback((id: string, title: string) => {
    setSessions((prev) => prev.map((s) => (s.id === id ? touch({ ...s, title: title || "Sem título" }) : s)));
  }, []);

  const remove = useCallback((id: string) => {
    setSessions((prev) => {
      const next = prev.filter((s) => s.id !== id);
      setActiveId((cur) => (cur === id ? next[0]?.id ?? null : cur));
      return next;
    });
  }, []);

  const addMessage = useCallback((sessionId: string, msg: ExplorerMessage) => {
    setSessions((prev) =>
      prev.map((s) => {
        if (s.id !== sessionId) return s;
        // Auto-título a partir da 1ª pergunta do usuário.
        const autoTitle =
          s.messages.length === 0 && msg.role === "user" && msg.text
            ? msg.text.slice(0, 48)
            : s.title;
        return touch({ ...s, title: autoTitle, messages: [...s.messages, msg] });
      }),
    );
  }, []);

  const patchMessage = useCallback(
    (sessionId: string, msgId: string, patch: Partial<ExplorerMessage>) => {
      setSessions((prev) =>
        prev.map((s) =>
          s.id === sessionId
            ? touch({ ...s, messages: s.messages.map((m) => (m.id === msgId ? { ...m, ...patch } : m)) })
            : s,
        ),
      );
    },
    [],
  );

  const active = sessions.find((s) => s.id === activeId) ?? null;
  return { sessions, activeId, active, storageFull, setActiveId, create, rename, remove, addMessage, patchMessage };
}
